import React from "react";
import PropTypes from "prop-types";
import { Icon } from "root/libs/ui-components/src";
import Illustration from "icons/cx/account-price-illustration.svg";
import { GetCountryCode } from "root/libs/core-libs/src";
import UserLoggedInIcon from "./UserLoggedIn";

import {
  Banner,
  CardContent,
  CardInfo,
  CardWrapper,
  InfoText,
  Note,
  OpenAccountButton,
  PriceRow,
  PriceRowContainer,
  PriceSection,
  SetupInfo,
  Title,
} from "./styled";
import AccountPriceHeadline from "../AccountPriceHeadline";
import PriceLabelTag from "./PriceLabelTag";
import NormalPriceRow from "./NormalPriceRow";
import { getCopy, getDiscountPercent } from "../helper";

const COPY_ID_PREFIX = "OfferAccountPrice";

const AccountPricingCard = ({
  intl,
  isCurrencyCodeAvailable,
  nativeLanguageCode,
  offer,
  offerIndex,
  offerPrice,
  priceLabel,
  showCurrencyIsoCodeForPrices,
  formatCurrencyWithNativeLocale,
}) => {
  const { priceFrequentShipper, priceWithoutDiscount } = offer;
  const countryCode = intl?.locale ? GetCountryCode(intl.locale) : "";

  if (!priceFrequentShipper || !priceWithoutDiscount) {
    return null;
  }

  return (
    <CardWrapper>
      <Banner>
        <UserLoggedInIcon />
        {getCopy(intl, `${COPY_ID_PREFIX}.banner`, {
          percentage: getDiscountPercent(
            priceWithoutDiscount.amount,
            priceFrequentShipper.amount
          ),
        })}
      </Banner>
      <CardContent>
        <CardInfo>
          <Icon useAs="decorative" icon={Illustration} size="xxxlarge" />
          <InfoText>
            <Title>{getCopy(intl, `${COPY_ID_PREFIX}.title`)}</Title>
            <SetupInfo>
              {getCopy(intl, `${COPY_ID_PREFIX}.setupInfo${countryCode}`)}
            </SetupInfo>
            <Note>{getCopy(intl, `${COPY_ID_PREFIX}.note`)}</Note>
          </InfoText>
        </CardInfo>
        <PriceSection>
          <PriceRow>
            <div>{getCopy(intl, `${COPY_ID_PREFIX}.priceFrom`)}</div>
            <PriceRowContainer>
              <PriceLabelTag
                intl={intl}
                priceWithoutDiscount={priceWithoutDiscount.amount}
                price={priceFrequentShipper.amount}
                variant="yellow"
              />
              <AccountPriceHeadline
                {...{
                  intl,
                  isCurrencyCodeAvailable,
                  nativeLanguageCode,
                  offer,
                  offerIndex,
                  offerPrice,
                  priceLabel,
                  showCurrencyIsoCodeForPrices,
                  formatCurrencyWithNativeLocale,
                }}
              />
            </PriceRowContainer>
          </PriceRow>
          <NormalPriceRow
            intl={intl}
            offer={offer}
            showCurrencyIsoCodeForPrices={showCurrencyIsoCodeForPrices}
          />
          <OpenAccountButton
            tabIndex={0}
            href={`https://www.dhl.com/${countryCode?.toLowerCase()}-en/home/request-a-business-account/dhl-express-for-businesses.html#fs-step=connectors`}
          >
            {getCopy(intl, `${COPY_ID_PREFIX}.actionAccountPrice`)}
          </OpenAccountButton>
        </PriceSection>
      </CardContent>
    </CardWrapper>
  );
};

AccountPricingCard.propTypes = {
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired,
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  isCurrencyCodeAvailable: PropTypes.bool,
  nativeLanguageCode: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.shape({
      currencyCode: PropTypes.string,
      currencyConversionFactor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    priceFrequentShipper: PropTypes.shape({
      amount: PropTypes.number,
    }),
    priceWithoutDiscount: PropTypes.shape({
      amount: PropTypes.number,
      currencyCode: PropTypes.string,
    }),
    euroPrice: PropTypes.shape({
      currencyCode: PropTypes.string,
      amount: PropTypes.number,
    }),
  }).isRequired,
  offerIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  offerPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  priceLabel: PropTypes.string,
  showCurrencyIsoCodeForPrices: PropTypes.bool,
  formatCurrencyWithNativeLocale: PropTypes.bool,
};

export default AccountPricingCard;
